import { useQuery } from "@tanstack/react-query";
import { CATALOGUE } from "../../config/catalogue";
import { api } from "../../services/api/endpoints";
import { useAuth } from "../auth/context";

/**
 * Server state. React Query owns caching; the components own none of it.
 */

/**
 * Keys for the two calls that return ONE ACCOUNT'S data — scoped by the account
 * they belong to (2026-08-10).
 *
 * They used to be the bare strings `["me"]` and `["entitlements"]`, which means
 * two different people share one cache entry. Nothing had gone wrong with that
 * yet, and the reason is worth stating because it is not a reason to leave it:
 * signing out in production ends in `window.location.assign` to Cognito's logout
 * URL, and a full navigation throws the JS heap away with the cache inside it.
 * That protection is INCIDENTAL. It is conditional on `DOMAIN && CLIENT_ID`
 * being configured (cognitoAdapter.logout), it does not happen at all under
 * mocks, and it is asynchronous — so "Switch account" on the account page runs
 * a live window where the app is signed out, still mounted, and still holding
 * the previous person's email and balance in cache.
 *
 * `enabled: false` does not help: a disabled query keeps returning its last
 * data for the whole gcTime (five minutes by default), so the queries going
 * quiet on sign-out does not make what they hold go away.
 *
 * AuthProvider also clears the whole cache on logout and on a 401, which is the
 * sweep that catches every other key. This is the structural half: with the
 * account in the key, one person's cached profile is not ADDRESSABLE as
 * another's, so it cannot be served to them by a code path that forgets to
 * clear. Ownership enforced by the key, not by an `if` a future edit can delete
 * — the same argument kev_history.py makes about the DynamoDB partition.
 *
 * `undefined` while signed out is fine and deliberate: both queries are
 * `enabled` only when authenticated, so the anonymous key is never fetched.
 */
export const meKey = (userId: string | undefined) => ["me", userId] as const;
export const entitlementsKey = (userId: string | undefined) =>
  ["entitlements", userId] as const;

/**
 * The catalogue comes from the API even though a copy is compiled in.
 *
 * Build status changes the day a model ships, and that must not require a
 * frontend redeploy. `initialData` from config means marketing pages paint
 * instantly with no loading flash, then revalidate against the server — the
 * static copy is a fast first frame, not the source of truth.
 */
export function useCatalogue() {
  return useQuery({
    queryKey: ["catalogue"],
    queryFn: api.catalogue,
    /* The compiled-in copy is a fast first frame, not the answer. It carries no
       capabilities (prompt chips are a serving concern), so treating it as fresh
       would leave the chat with no chips until something else triggered a
       refetch. `initialDataUpdatedAt: 0` marks it stale on arrival: instant
       paint, immediate revalidate. */
    initialData: { models: CATALOGUE.map((m) => ({ ...m, capabilities: [] })) },
    initialDataUpdatedAt: 0,
    staleTime: 5 * 60_000,
  });
}

/**
 * Entitlements are server-authoritative and always fetched — never derived from
 * a JWT claim. A Stripe webhook can change them mid-session; a token claim would
 * be stale until refresh.
 *
 * `refetchOnWindowFocus` (set globally) is what catches the user who buys in
 * another tab, or whose subscription lapses while they sit on the page.
 */
export function useEntitlements() {
  const { status, session } = useAuth();
  return useQuery({
    queryKey: entitlementsKey(session?.userId),
    queryFn: api.entitlements,
    enabled: status === "authenticated",
    staleTime: 15_000,
  });
}

export function useMe() {
  const { status, session } = useAuth();
  return useQuery({
    queryKey: meKey(session?.userId),
    queryFn: api.me,
    enabled: status === "authenticated",
  });
}
